import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import type { DrivingTestsMathsRoute } from 'teacher-ui-client/reporting/ui-scope/maths/driving-tests/route'
import type { DrivingTestsMathsController } from 'teacher-ui-client/reporting/ui-scope/maths/driving-tests/controller'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { DrivingTestsTotals } from 'teacher-ui-client/components/giraffe/charts/driving-tests/totals'
import { DrivingTestsHistory } from 'teacher-ui-client/components/giraffe/charts/driving-tests/history'
import { camelize } from '@ember/string'
import { not, or } from 'ember-truth-helpers'
import { array, concat } from '@ember/helper'
import { t } from 'ember-intl'
import isSubco from 'teacher-ui-client/helpers/is-subco'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { service } from '@ember/service'

import type MathsDrivingTestsService from 'teacher-ui-client/services/maths-driving-tests'

interface Signature {
  model: ModelFor<DrivingTestsMathsRoute>
  controller: DrivingTestsMathsController
}

export class MathsDrivingTests extends Component<Signature> {
  @service mathsDrivingTests!: MathsDrivingTestsService

  get currentDrivingTestsGradeName() {
    return this.mathsDrivingTests.drivingTestData.find((dtData) => dtData.drivingTestGrade)?.fullName
  }

  get totalsChartData() {
    return this.args.model.drivingTestsTotals?.find(
      (data) => data.driving_test_grade === this.args.controller.drivingTestsGrade,
    )
  }

  get historyChartData() {
    return this.args.model.drivingTestsHistory?.find(
      (data) => data.driving_test_grade === this.args.controller.drivingTestsGrade,
    )
  }

  get drivingTestCounts() {
    switch (this.args.controller.drivingTestsGrade) {
      case 0:
        return {
          numberCount: 25,
          operationsCount: 25,
          patternsCount: 9,
          geometryCount: 23,
          dataCount: 10,
          measurementCount: 20,
        }
      case 1:
        return {
          numberCount: 24,
          operationsCount: 20,
          patternsCount: 14,
          geometryCount: 18,
          dataCount: 16,
          measurementCount: 19,
        }
      case 2:
        return {
          numberCount: 24,
          operationsCount: 28,
          patternsCount: 17,
          geometryCount: 13,
          dataCount: 14,
          measurementCount: 24,
        }
      default:
        return {
          numberCount: 0,
          operationsCount: 0,
          patternsCount: 0,
          geometryCount: 0,
          dataCount: 0,
          measurementCount: 0,
        }
    }
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.drivingTests"}} />
    <ReportingDetailedControls
      @students={{@model.scopedStudents}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{if (or this.totalsChartData this.historyChartData) true false}}
      @showScopePicker={{true}}
      @showDatePicker={{true}}
      @drivingTestsGrade={{@controller.drivingTestsGrade}}
      @setDrivingTestsGrade={{@controller.setDrivingTestsGrade}}
      @studentRouteName="reporting.ui-scope.maths.student-summary"
    />
    <ReportingNoDataMessageIf @on={{not (or this.totalsChartData this.historyChartData)}}>
      <h3 class="hidden text-center print:block">{{this.currentDrivingTestsGradeName}}</h3>
      <div
        class="border-dusty-black-50 shadow-dusty-black-100 print:border-dusty-black-200 my-10 h-76 rounded-md border bg-white shadow-md print:rounded-none print:shadow-none"
      >
        {{#if this.totalsChartData}}
          <DrivingTestsTotals
            @data={{this.totalsChartData}}
            @product={{@model.product}}
            @summaryTitle={{t (concat "reporting.summaryTitles.drivingTestsTotals." (camelize @model.uiScope.scope))}}
            @title={{t "reporting.chartTitles.drivingTestsTotals"}}
            @tooltip={{t "reporting.tooltips.drivingTestsTotals.maths"}}
          />
        {{/if}}
      </div>

      <div class="my-10">
        {{#if this.historyChartData}}
          <DrivingTestsHistory
            @students={{@model.scopedStudents}}
            @subco={{(isSubco)}}
            @drivingTestsHistoryData={{this.historyChartData}}
            @uiScope={{@model.uiScope.scope}}
            @studentRouteName="reporting.ui-scope.maths.student-summary"
            @drivingTestsGrade={{@controller.drivingTestsGrade}}
            @product={{@model.product}}
            @tooltip={{t
              "reporting.tooltips.drivingTestsHistory.maths"
              htmlSafe=true
              numberCount=this.drivingTestCounts.numberCount
              operationsCount=this.drivingTestCounts.operationsCount
              patternsCount=this.drivingTestCounts.patternsCount
              geometryCount=this.drivingTestCounts.geometryCount
              dataCount=this.drivingTestCounts.dataCount
              measurementCount=this.drivingTestCounts.measurementCount
            }}
          />
        {{/if}}
      </div>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(MathsDrivingTests)
